import * as React from 'react';
import { useEffect, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';

const emptyForm = {
  asset_type_code: '',
  display_name: '',
  description: '',
  category: 'asset',
  status: 'active',
  metadata: '{}',
};

function typeToForm(assetType) {
  return {
    asset_type_code: assetType?.asset_type_code || '',
    display_name: assetType?.display_name || '',
    description: assetType?.description || '',
    category: assetType?.category || 'asset',
    status: assetType?.status || 'active',
    metadata: JSON.stringify(assetType?.metadata || {}, null, 2),
  };
}

function parseMetadata(value) {
  if (!value || !value.trim()) {
    return {};
  }
  return JSON.parse(value);
}

export default function AdminAssetTypesPage(props) {
  const [state, setState] = useState({ loading: true, error: '', assetTypes: [] });
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [feedback, setFeedback] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saving, setSaving] = useState(false);

  async function loadAssetTypes() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get('/admin/asset-types', {
        params: { include_retired: true },
      });
      setState({
        loading: false,
        error: '',
        assetTypes: Array.isArray(response.data?.asset_types) ? response.data.asset_types : [],
      });
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load asset types.',
        assetTypes: [],
      });
    }
  }

  useEffect(() => {
    loadAssetTypes();
  }, []);

  async function handleCreate(event) {
    event.preventDefault();
    setSaving(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.post('/admin/asset-types', {
        asset_type_code: form.asset_type_code,
        display_name: form.display_name,
        description: form.description || null,
        category: form.category,
        status: form.status,
        metadata: parseMetadata(form.metadata),
      });
      setForm(emptyForm);
      setFeedback('Asset type created.');
      await loadAssetTypes();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || 'Unable to create asset type.');
    } finally {
      setSaving(false);
    }
  }

  function openEditor(assetType) {
    setEditing(assetType);
    setEditForm(typeToForm(assetType));
  }

  function closeEditor() {
    if (saving) {
      return;
    }
    setEditing(null);
    setEditForm(emptyForm);
  }

  async function handleUpdate() {
    if (!editing) {
      return;
    }
    setSaving(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.patch(`/admin/asset-types/${editing.asset_type_code}`, {
        display_name: editForm.display_name,
        description: editForm.description || null,
        category: editForm.category,
        status: editForm.status,
        metadata: parseMetadata(editForm.metadata),
      });
      setFeedback('Asset type updated.');
      closeEditor();
      await loadAssetTypes();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || 'Unable to update asset type.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(assetType) {
    if (!window.confirm(`Delete asset type "${assetType.display_name}"?`)) {
      return;
    }
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(`/admin/asset-types/${assetType.asset_type_code}`);
      setFeedback('Asset type deleted.');
      await loadAssetTypes();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete asset type.');
    }
  }

  const formFields = (currentForm, setCurrentForm, includeCode) => (
    <Stack spacing={2}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {includeCode ? (
          <TextField
            label="Asset Type Code"
            value={currentForm.asset_type_code}
            onChange={(event) => setCurrentForm({ ...currentForm, asset_type_code: event.target.value })}
            fullWidth
            required
            helperText="Lowercase topic-safe code, for example operational_asset."
          />
        ) : null}
        <TextField
          label="Display Name"
          value={currentForm.display_name}
          onChange={(event) => setCurrentForm({ ...currentForm, display_name: event.target.value })}
          fullWidth
          required
        />
        <TextField
          label="Category"
          select
          value={currentForm.category}
          onChange={(event) => setCurrentForm({ ...currentForm, category: event.target.value })}
          fullWidth
        >
          <MenuItem value="asset">asset</MenuItem>
          <MenuItem value="capability_asset">capability_asset</MenuItem>
          <MenuItem value="sensor">sensor</MenuItem>
          <MenuItem value="operational_asset">operational_asset</MenuItem>
          <MenuItem value="facility">facility</MenuItem>
        </TextField>
        <TextField
          label="Status"
          select
          value={currentForm.status}
          onChange={(event) => setCurrentForm({ ...currentForm, status: event.target.value })}
          fullWidth
        >
          <MenuItem value="active">active</MenuItem>
          <MenuItem value="inactive">inactive</MenuItem>
          <MenuItem value="retired">retired</MenuItem>
        </TextField>
      </Stack>
      <TextField
        label="Description"
        value={currentForm.description}
        onChange={(event) => setCurrentForm({ ...currentForm, description: event.target.value })}
        fullWidth
        multiline
        minRows={2}
      />
      <TextField
        label="Metadata JSON"
        value={currentForm.metadata}
        onChange={(event) => setCurrentForm({ ...currentForm, metadata: event.target.value })}
        fullWidth
        multiline
        minRows={3}
      />
    </Stack>
  );

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[
        { label: 'AMX xConsole', href: '/' },
        { label: 'Admin Dashboard', href: '/admin' },
        { label: 'Asset Types' },
      ]}
      title="Asset Types"
      subtitle="Manage the asset type registry used by partner assets and telemetry topics."
    >
      {feedback ? <Alert severity="success">{feedback}</Alert> : null}
      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <AdminSection title="Create Asset Type" subtitle="Add supported asset types for capability assets, sensors, and operational equipment.">
        <Stack component="form" spacing={2} onSubmit={handleCreate}>
          {formFields(form, setForm, true)}
          <Stack direction="row" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={saving}>
              {saving ? 'Saving...' : 'Create Asset Type'}
            </Button>
          </Stack>
        </Stack>
      </AdminSection>

      <AdminSection title="Asset Type Registry" subtitle="System types can be edited, but not deleted.">
        {state.loading ? (
          <Stack direction="row" spacing={2} alignItems="center">
            <CircularProgress size={24} />
            <Typography>Loading asset types...</Typography>
          </Stack>
        ) : state.error ? (
          <Alert severity="error">{state.error}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {state.assetTypes.map((assetType) => (
                <TableRow key={assetType.asset_type_code} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {assetType.display_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {assetType.asset_type_code}
                    </Typography>
                  </TableCell>
                  <TableCell>{assetType.category}</TableCell>
                  <TableCell>
                    <Chip size="small" label={assetType.status} variant="outlined" />
                  </TableCell>
                  <TableCell>{assetType.description || '-'}</TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button size="small" variant="outlined" onClick={() => openEditor(assetType)}>
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={assetType.is_system}
                        onClick={() => handleDelete(assetType)}
                      >
                        Delete
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </AdminSection>

      <Dialog open={Boolean(editing)} onClose={closeEditor} fullWidth maxWidth="md">
        <DialogTitle>Edit Asset Type</DialogTitle>
        <DialogContent dividers>
          {formFields(editForm, setEditForm, false)}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Asset Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
}
